// External Dependencies
import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'

// Icons
import { MdOutlineDelete } from 'react-icons/md'
import { CiEdit } from 'react-icons/ci'

// Components
import Button from '../Custom/Buttons/Button'
import CustomToast from '../Custom/CustomToast'

// Utils and Hooks
import axiosInterceptorInstance from '../../utils/axios/axiosInterceptorInstance'
import { useDebounce } from '../../hooks/useDebounce'
import configManager from '../../config/configManager'

// Assets
import brokenImg from '../../assets/images/brokenImg.png'

// Constants
const PATTERN_CATEGORIES = [
  { value: null, label: 'Select Category', disabled: true, selected: true },
  { value: 'Brick Bond', label: 'Brick Bond' },
  { value: 'Paving', label: 'Paving' },
  { value: 'Parquetry', label: 'Parquetry' },
  { value: 'Geometric', label: 'Geometric' },
  { value: 'Organic', label: 'Organic' },
  { value: 'Random', label: 'Random' },
  { value: 'Roofing', label: 'Roofing' },
]

const ITEMS_PER_PAGE = 50

const Patterns = ({ userPermissions }) => {
  const configData = configManager()
  const [loading, setLoading] = useState(false)
  const [category, setSelectedCategory] = useState(null)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [patterns, setPatterns] = useState([])
  const [moreAvailable, setMoreAvailable] = useState(true)
  const [deletePatterns, setDeletePatterns] = useState([])
  const [isDeleting, setIsDeleting] = useState(false)

  const debouncedSearchTerm = useDebounce(search, 300)

  // Fetch patterns when search or category changes
  useEffect(() => {
    setPage(1)
    setPatterns([])
    fetchPatterns({
      page: 1,
      limit: ITEMS_PER_PAGE,
      category: category === 'Select Category' ? null : category,
      search: debouncedSearchTerm,
    })
  }, [debouncedSearchTerm, category])

  // API Handlers
  const fetchPatterns = async (params) => {
    const urlParams = new URLSearchParams({
      page: params.page || 1,
      limit: params.limit || ITEMS_PER_PAGE,
      front: false,
      ...(params.category && { category: params.category }),
      ...(params.search && { search: params.search }),
    })

    setLoading(true)
    try {
      const { data } = await axiosInterceptorInstance.post(
        `/patterns?${urlParams.toString()}`,
        { body: JSON.stringify(params) },
      )

      if (data?.status === 'success') {
        setPatterns((prev) =>
          params.page === 1 ? data.results : [...prev, ...data.results],
        )
        setMoreAvailable(data.more)
      } else {
        CustomToast({
          message: 'Failed to fetch patterns',
          type: 'error',
        })
      }
    } catch (error) {
      CustomToast({
        message: 'An error occurred while fetching patterns',
        type: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  // Delete Handlers
  const handleDeletePatterns = async () => {
    if (!userPermissions.includes('pattern:delete')) {
      CustomToast({
        message: 'You do not have permission to delete patterns',
        type: 'error',
      })
      return
    }

    if (!deletePatterns.length) {
      CustomToast({
        message: 'Please select patterns to delete',
        type: 'warning',
      })
      return
    }

    setIsDeleting(true)
    try {
      const response = await axiosInterceptorInstance.delete(
        '/patterns/delete',
        {
          data: { deletePatterns },
        },
      )

      if (response.data.success) {
        setPatterns((prev) =>
          prev.filter((pattern) => !deletePatterns.includes(pattern._id)),
        )
        CustomToast({
          message: 'Patterns deleted successfully',
          type: 'success',
        })
      }
    } catch (error) {
      CustomToast({
        message: 'Failed to delete patterns. Please try again',
        type: 'error',
      })
    } finally {
      setIsDeleting(false)
      setDeletePatterns([])
    }
  }

  // Event Handlers
  const handleLoadMore = () => {
    const nextPage = page + 1
    setPage(nextPage)
    fetchPatterns({
      page: nextPage,
      limit: ITEMS_PER_PAGE,
      category: category === 'Select Category' ? null : category,
      search: debouncedSearchTerm,
    })
  }

  const handleSelectForDelete = (patternId) => {
    if (!userPermissions.includes('pattern:delete')) return

    setDeletePatterns((prev) =>
      prev.includes(patternId)
        ? prev.filter((id) => id !== patternId)
        : [...prev, patternId],
    )
  }

  const handleSelectAllForDelete = () => {
    if (!userPermissions.includes('pattern:delete')) return

    setDeletePatterns((prev) =>
      prev.length === patterns.length
        ? []
        : patterns.map((pattern) => pattern._id),
    )
  }

  // Render Methods
  const renderDeleteControls = () => {
    if (!deletePatterns.length || !userPermissions.includes('pattern:delete')) {
      return null
    }

    return (
      <>
        <Button
          label='Delete'
          disabled={isDeleting}
          isLoading={isDeleting}
          onClick={handleDeletePatterns}
          className='flex gap-1.5 items-center font-medium rounded-lg disabled:pointer-events-none disabled:opacity-70 border-2 !border-red-700 hover:!bg-red-700 !text-red-700 hover:!text-white'
        >
          <MdOutlineDelete className='text-2xl' />
        </Button>
        <label className='flex gap-2 items-center font-semibold'>
          <input
            className='w-5 h-5'
            type='checkbox'
            checked={deletePatterns.length === patterns.length}
            onChange={handleSelectAllForDelete}
          />
          Select All
        </label>
      </>
    )
  }

  return (
    <div>
      <div className='flex gap-4 justify-start items-center flex-wrap'>
        {renderDeleteControls()}
        <div className='ml-auto'>
          <select
            className='w-full border rounded-lg px-3 py-3 focus:outline-none focus:ring-2 focus:ring-prime'
            value={category || ''}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            {PATTERN_CATEGORIES.map((cat) => (
              <option
                key={cat.value || 'default'}
                value={cat.value || ''}
                disabled={cat.disabled}
              >
                {cat.label}
              </option>
            ))}
          </select>
        </div>
        <input
          placeholder='Search patterns...'
          className='w-full sm:w-[300px] border-[2px] border-prime rounded-lg p-3 focus:outline-none focus:border-orange'
          type='text'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {loading && <p className='text-center my-20'>Loading patterns...</p>}

      {!loading && patterns.length === 0 && (
        <p className='text-center my-10'>No patterns available.</p>
      )}

      <div className='grid  grid-cols-2 sm:grid-cols-4 md:grid-cols-5 justify-around gap-4 my-8'>
        {patterns.map((pattern) => (
          <div
            key={pattern._id}
            className='
          m-4 text-center'
          >
            <div className='relative group w-36 h-36 mx-auto'>
              {userPermissions.includes('pattern:update') ? (
                <Link
                  to={`/pattern/update/${pattern._id}`}
                  className='absolute bottom-2 right-2 bg-prime p-2 text-xl hidden text-white rounded-full group-hover:inline-block '
                >
                  <CiEdit />
                </Link>
              ) : null}
              <img
                className={`w-full h-full object-cover  bg-gray-50 ${deletePatterns?.includes(pattern._id)
                    ? 'border-4 border-prime '
                    : ''
                  }`}
                src={
                  pattern.stringId
                    ? `${configData.CDN_BASE_URL}/${pattern.stringId.startsWith('/')
                      ? pattern.stringId.slice(1)
                      : 'patterns/' + pattern.stringId + '.svg'
                    }`
                    : `${configData.CDN_BASE_URL}/img/home/add1.png`
                }

                onError={(e) => (e.target.src = brokenImg)}
                alt={pattern.name || 'Material Image'}
                onClick={() =>
                  userPermissions.includes('pattern:delete')
                    ? handleSelectForDelete(pattern._id)
                    : null
                }
              />
              {userPermissions.includes('pattern:delete') ? (
                <input
                  type='checkbox'
                  className={`w-4 h-4 absolute top-4 right-4 ${deletePatterns?.includes(pattern._id) ? 'block' : 'hidden'
                    }`}
                  checked={deletePatterns?.includes(pattern._id)}
                  onChange={() => handleSelectForDelete(pattern._id)}
                />
              ) : null}
            </div>
            <div className='py-3 px-2'>
              {pattern.name && (
                <div className='font-semibold'>{pattern.name}</div>
              )}
              {pattern.category && (
                <div className='text-gray-600'>{pattern.category}</div>
              )}
            </div>
          </div>
        ))}
      </div>

      {moreAvailable && !loading && (
        <div className='flex justify-center mt-8'>
          <Button
            label='Load More'
            onClick={handleLoadMore}
            className='bg-prime text-white px-6 py-2 rounded-lg hover:bg-prime/90'
          />
        </div>
      )}
    </div>
  )
}

export default Patterns
